import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import sortBy from "lodash/sortBy";

import { formatNumber, formatBytes } from "utils/format";
import { useRoutes } from "utils/routes";
import Loading from "components/Loading";
import PageContent from "components/PageContent";
import Panel from "components/Panel";

import {
  ServerSchemaOverview as ServerSchemaOverviewType,
  ServerSchemaOverviewVariables,
  ServerSchemaOverview_getDatabaseStats as DatabaseStats,
} from "./types/ServerSchemaOverview";
import QUERY from "./Query.graphql";

import Grid from "components/Grid";
import { makeFilter } from "utils/filter";
import { useQueryStringSearch } from "components/QueryStringSearch";

const ServerSchemaOverview: React.FunctionComponent = () => {
  const { serverId } = useParams();
  const [searchTerm, SearchInput] = useQueryStringSearch("q");

  const { data, loading, error } = useQuery<
    ServerSchemaOverviewType,
    ServerSchemaOverviewVariables
  >(QUERY, {
    variables: {
      serverId,
    },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  return (
    <PageContent
      title="Schema Statistics"
      pageCategory="schema"
      pageName="index_server"
    >
      <Panel title="Databases" secondaryTitle={<SearchInput />}>
        <Table data={data} searchTerm={searchTerm} />
      </Panel>
    </PageContent>
  );
};

type RowDataType = {
  databaseId: string | null | undefined;
  databaseName: string | null | undefined;
} & DatabaseStats;

type TableProps = {
  data: ServerSchemaOverviewType;
  searchTerm: string;
};

const Table: React.FunctionComponent<TableProps> = ({ data, searchTerm }) => {
  let tableData = data.getDatabaseStats.map(
    (inputData: DatabaseStats): RowDataType => {
      return {
        ...inputData,
        databaseId: inputData.database?.id ?? null,
        databaseName: inputData.database?.datname ?? null,
      };
    },
  );

  tableData = tableData.filter(makeFilter(searchTerm, "databaseName"));
  tableData = sortBy(tableData, (b: RowDataType): number => -b.totalSize);

  return (
    <Grid
      striped
      data={tableData}
      defaultSortBy="totalSize"
      columns={[
        {
          field: "databaseName",
          header: "Database",
          width: "1fr",
          renderer: DatabaseNameCell,
        },
        {
          field: "tableViewCount",
          header: "Table + View Count",
          width: "160px",
          style: "number",
          defaultSortOrder: "DESC",
          nullValue: "n/a",
          renderer: function TableViewCountCell({ fieldData }) {
            return formatNumber(fieldData);
          },
        },
        {
          field: "dataSize",
          header: "Data Size",
          width: "minmax(10%,120px)",
          style: "number",
          defaultSortOrder: "DESC",
          nullValue: "n/a",
          renderer: BytesCell,
        },
        {
          field: "toastSize",
          header: "TOAST Size",
          width: "minmax(10%,120px)",
          style: "number",
          defaultSortOrder: "DESC",
          nullValue: "n/a",
          renderer: BytesCell,
        },
        {
          field: "indexSize",
          header: "Index Size",
          width: "minmax(10%,120px)",
          style: "number",
          defaultSortOrder: "DESC",
          nullValue: "n/a",
          renderer: BytesCell,
        },
        {
          field: "totalSize",
          header: "Total Size",
          width: "minmax(10%,120px)",
          style: "number",
          defaultSortOrder: "DESC",
          nullValue: "n/a",
          renderer: BytesCell,
        },
      ]}
    />
  );
};

const BytesCell: React.FunctionComponent<{ fieldData: number }> = ({
  fieldData,
}) => {
  return <>{formatBytes(fieldData)}</>;
};

const DatabaseNameCell: React.FunctionComponent<{ rowData: RowDataType }> = ({
  rowData,
}) => {
  const { databaseTables } = useRoutes();
  return (
    <span>
      {rowData.database && !rowData.database.hidden && (
        <Link to={databaseTables(rowData.database.id)}>
          {rowData.database.datname}
        </Link>
      )}
      {rowData.database && rowData.database.hidden && rowData.database.datname}
    </span>
  );
};

export default ServerSchemaOverview;
